import {
  cellAt,
  cellOutline,
  createRenderer,
  createRendererSource,
  destroyRendererSource,
  destroyUnused,
  exceedsTextureLimit,
  fileKeyOf,
  findShared,
  isStillUsed,
  isVideoSource,
  naturalSizeOf,
  sourceKindOf,
  tryPlay,
  waitForNaturalSize,
  type DisplayTransform,
  type Renderer,
  type RendererSource,
} from '@signa/renderer';
import {
  cellCount,
  cellKey,
  correctionsFor,
  outlinePoints,
  pickCell,
  toNormalized,
  type CellRef,
  type FrameLike,
  type NormalizedPoint,
  type PublicTemplate,
} from '@signa/shared';
import {
  BACKGROUND_RETRY_DELAYS,
  fetchTemplate,
  loadImage,
  VIEWER_ERROR_CODES,
  ViewerError,
  type ViewerErrorCode,
} from './api.js';
import { errorMessage, isRetryable, messages, type Lang } from './messages.js';
import {
  autoStartedPlayback,
  controlsState,
  joinPositionOf,
  playableOf,
  showsNoUploadNotice,
  startedPlayback,
  stoppedPlayback,
  STOPPED_PLAYBACK,
  type PlaybackState,
} from './playback.js';

import { viewerStyles, VIEWPORT_HEIGHT_LIMITS } from './styles.js';

/**
 * ビューアシェル。
 *
 * **V01(embed.js)と V02(単体ページ)の共有部分**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。
 * 差分は「入口(トークンの取得方法)と外枠」だけなので、ここに寄せる。
 *
 * 責務:
 * - テンプレートの取得と状態表示(ローディング / エラー / 再試行)
 * - 素材(動画・画像)の D&D とセルへの割り当て(**当てる手段は D&D だけ** —— #159)
 * - 描画コアの生死管理
 *
 * **素材の割り当て状態はここが保持する。** コンテキストロストで Renderer の
 * 内部状態が失われても、ここから `setSource` を再実行すれば復帰できる(#33)。
 */

export interface ShellOptions {
  readonly token: string;
  readonly apiBase: string;
  readonly fit?: 'contain' | 'cover';
  readonly maxWidth?: number | null;
  /**
   * **ビューポートの高さに収める**(#212)。**V02(プレビュー URL)だけが渡す。**
   *
   * 既定(V01 = 埋め込み)では**幅にしか追従しない** —— 高さは
   * [`applyAspectRatio`](#applyAspectRatio) が縦横比から導く。埋め込み先の
   * ページには他の要素があり、**こちらが画面の高さを占有してはいけない。**
   *
   * V02 は**盤面だけを見せる画面**なので事情が逆で、**画面に収まっていないと
   * スクロールしないと全体が見えない** —— 16:9 の画面で 16:9 の盤面を開くと、
   * **ブラウザ UI の分だけ必ず縦に溢れていた。**
   */
  readonly fitViewport?: boolean;
  readonly autoplay?: boolean;
  readonly lang?: Lang;
  /** 状態変化の通知。V01 は `CustomEvent` に変換する。V02 は使わない。 */
  readonly onEvent?: (event: ShellEvent) => void;
}

export type ShellEvent =
  | { readonly type: 'ready'; readonly frameCount: number }
  | { readonly type: 'error'; readonly code: ViewerErrorCode }
  | {
      readonly type: 'videoassigned';
      readonly frameId: string;
      /** 枠の中のセル番号(#152)。**分割していない枠は `0`。** */
      readonly cellIndex: number;
      readonly frameCount: number;
      /**
       * 盤面のセルの総数(#152)。**分割していない枠は 1 つ数える。**
       *
       * **`assignedCount` と単位をそろえるために足した。** #152 で
       * 割り当てがセル単位になり、`assignedCount` が `frameCount` を
       * 超えうるようになった —— 「全部埋まったか」を
       * `assignedCount === frameCount` で判定していると壊れる。
       */
      readonly cellCount: number;
      /** 割り当て済みの**セル**の数(#152 で枠からセルへ)。 */
      readonly assignedCount: number;
    }
  | { readonly type: 'play'; readonly frameId: string }
  | { readonly type: 'pause'; readonly frameId: string };

interface Assignment extends CellRef {
  /** 動画または画像(#158)。**同じファイルなら複数のセルで共有する**(#168)。 */
  readonly source: RendererSource;
  /**
   * 出どころのファイルの鍵(#168)。
   *
   * **同じファイルを 2 度ドロップしたときに要素を使い回す**ために持つ ——
   * ドロップごとに `<video>` を作ると**復号器がセルの数だけ立つ。**
   */
  readonly fileKey: string;
}

/**
 * 盤面のセルの総数(#152)。**分割していない枠も 1 つ数える。**
 *
 * 公開イベントの `assignedCount` と単位をそろえるために出す ——
 * 埋め込み先が「全部埋まったか」を数で判定できるようにする。
 */
function totalCells(frames: readonly FrameLike[]): number {
  return frames.reduce((sum, frame) => sum + (frame.grid == null ? 1 : cellCount(frame.grid)), 0);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

export class ViewerShell {
  private readonly lang: Lang;
  private readonly root: HTMLDivElement;
  private readonly frame: HTMLDivElement;
  private readonly stageHost: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private readonly ui: HTMLDivElement;
  private readonly dropzone: HTMLDivElement;
  /** ドロップ先の枠を縁取る SVG(#124)。 */
  private readonly dropOutline: SVGSVGElement;
  private readonly dropOutlineShape: SVGPolygonElement;
  private readonly message: HTMLDivElement;
  private readonly videoHost: HTMLDivElement;

  private renderer: Renderer | null = null;
  private template: PublicTemplate | null = null;
  private transform: DisplayTransform = { width: 0, height: 0, offsetX: 0, offsetY: 0 };
  private readonly assignments = new Map<string, Assignment>();

  /**
   * 再生・停止の見張りを張り終えた素材(#168)。
   *
   * **共有すると同じ要素に何度も張ってしまう。** `WeakSet` なので
   * 素材を破棄すれば自然に落ちる。
   */
  private readonly watched = new WeakSet<RendererSource>();
  private destroyed = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  // --- 再生の操作(#159)---------------------------------------------------
  private readonly notice: HTMLDivElement;
  private readonly actions: HTMLDivElement;
  private readonly controls: HTMLDivElement;
  private readonly playButton: HTMLButtonElement;
  private readonly pauseButton: HTMLButtonElement;
  private readonly syncButton: HTMLButtonElement;
  /**
   * 再生の状態(#159)。**`playing` と時間軸を一緒に動かす。**
   *
   * 別々に持つと「再生中なのに時間軸が止まっている」という食い違いができ、
   * **次の「再生」で全部が頭へ巻き戻る。** 遷移は
   * [`playback.ts`](./playback.ts) の純関数に置いてある。
   *
   * **`<video>` どうしを直接比べない。** 長さが違うとループを跨いだ瞬間に
   * 巻き戻り、同じ位置なのに大きくずれて見える(#123)。
   */
  private playback: PlaybackState = STOPPED_PLAYBACK;

  constructor(
    container: ParentNode & { append: (...nodes: Node[]) => void },
    private readonly options: ShellOptions,
  ) {
    this.lang = options.lang ?? 'ja';
    const m = messages(this.lang);

    const style = document.createElement('style');
    style.textContent = viewerStyles();

    this.root = el('div', 'signa-root');
    if (typeof options.maxWidth === 'number') {
      this.root.style.maxWidth = `${String(options.maxWidth)}px`;
    }

    this.frame = el('div', 'signa-frame');
    this.stageHost = el('div', 'signa-stage-host');
    // 高さは `aspect-ratio` で決める(`applyAspectRatio`)。**`height: 100%`
    // にはしない** —— 親の高さが中身依存なので解決できず 0 になる。
    this.stageHost.setAttribute(
      'style',
      'display: flex; align-items: center; justify-content: center; width: 100%;',
    );
    if (options.fitViewport === true) this.limitToViewport();

    this.status = el('div', 'signa-status');
    this.status.textContent = m.loading;

    /*
     * **`ui` そのものは隠さない**(#159 の退行の原因)。
     *
     * `ui` は D&D の受け口を含む。読み込み中に隠すつもりで `hidden` にすると、
     * **戻し忘れたときにドロップがページへ素通りし、ブラウザがファイルを
     * 別タブで開く。** 隠すのは中身(受け口と操作の帯)だけにする。
     */
    this.ui = el('div', 'signa-ui');

    this.dropzone = el('div', 'signa-dropzone');
    this.dropzone.dataset.active = 'false';
    const dropLabel = el('div', 'signa-dropzone-label');
    dropLabel.textContent = m.dropHint;
    this.dropzone.append(dropLabel);

    /*
     * **ドロップ先のセルだけを縁取る**(#124 → #152)。
     *
     * 以前は `signa-dropzone` が画面全体(`inset: 0`)を縁取っていたが、
     * **どの枠に入るのかが分からなかった。** 落とす前に入る先を見せる。
     * 分割した盤面では枠でも足りないので、**セルの輪郭**を出す。
     *
     * **`pickCell` の規則は変えない**(#33)—— 枠の外に落としても
     * 空きセルに入る。その行き先がここに出るので、
     * **フォールバックが目に見えるようになる。**
     */
    this.dropOutline = document.createElementNS(SVG_NS, 'svg');
    this.dropOutline.setAttribute('class', 'signa-drop-outline');
    this.dropOutline.setAttribute('aria-hidden', 'true');
    this.dropOutlineShape = document.createElementNS(SVG_NS, 'polygon');
    this.dropOutlineShape.setAttribute('class', 'signa-drop-outline-shape');
    this.dropOutline.append(this.dropOutlineShape);
    this.dropzone.append(this.dropOutline);

    this.notice = el('div', 'signa-notice');
    this.notice.textContent = m.noUpload;

    this.message = el('div', 'signa-message');
    this.message.hidden = true;

    /*
     * 再生 / 停止 / 同期(#159)。
     *
     * **素材が当たるまで出さない。** 押しても何も起きないボタンは
     * 「壊れている」と読める。**静止画だけのときも出さない**(#158)——
     * `<img>` に `currentTime` は無い。
     *
     * **`autoplay: false` でも出す。** 自動で始まらないだけで、
     * 閲覧者が自分で流せるべきである。
     */
    this.controls = el('div', 'signa-controls');
    this.playButton = this.makeControl(m.play, () => {
      void this.playAll(false);
    });
    this.pauseButton = this.makeControl(m.pause, () => {
      this.pauseAll();
    });
    this.syncButton = this.makeControl(m.sync, () => {
      void this.playAll(true);
    });
    this.controls.append(this.playButton, this.pauseButton, this.syncButton);

    this.actions = el('div', 'signa-actions');
    this.actions.append(this.controls, this.notice);
    this.ui.append(this.dropzone, this.message, this.actions);

    this.videoHost = el('div', 'signa-videos');

    this.frame.append(this.stageHost, this.status, this.ui);
    this.root.append(this.frame, this.videoHost);
    container.append(style, this.root);

    /*
     * **最初の状態をそろえる**(#159)。
     *
     * 受け口はテンプレートを読むまで出さない(枠があるか分からない)。
     * 素材は 0 件なので、操作は出さず注意は出す。
     */
    this.showDropSurface(false);
    this.updateControls();

    this.bindDragAndDrop();
  }

  /** 取得と描画を開始する。 */
  async start(): Promise<void> {
    this.showLoading();
    try {
      this.template = await fetchTemplate(this.options.apiBase, this.options.token);
    } catch (error) {
      this.fail(error instanceof ViewerError ? error.code : VIEWER_ERROR_CODES.NETWORK);
      return;
    }
    if (this.destroyed) return;

    await this.mountRenderer(this.template);
  }

  /**
   * ステージの高さをキャンバス比から決める。
   *
   * **幅は埋め込み先が決め、高さは比で決まる**([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md)
   * の「`canvas.width` / `canvas.height` はアスペクト比の算出に使う。
   * 表示サイズの決定には使わない(埋め込み先の幅に追従するため)」)。
   *
   * **高さを中身依存にしてはいけない。** 描画コアは親を測ってステージの
   * 寸法を決めるため(packages/renderer/src/webgl-renderer.ts の `resize`)、
   * 「親の高さ ← 中身 ← 親の測定値」で循環し**永久に 0 のまま**になる
   * (#84 の通し確認で踏んだ)。
   */
  /**
   * **ステージの高さをビューポートに収める**(#212)。**V02 だけで呼ぶ。**
   *
   * ## なぜ `aspect-ratio` だけでは足りなかったか
   *
   * [`applyAspectRatio`](#applyAspectRatio) は `width: 100%` と縦横比から
   * **高さを導く。** 幅は満たせるが、**ビューポートの高さは計算に入らない** ——
   * 16:9 の画面(1920×1080)でブラウザ UI が 100px を使うと、
   * ビューポートは 1920×980 なのに高さは `1920 × 9 / 16 = 1080` になり、
   * **必ず 100px 溢れていた。**
   *
   * ## 縦横比は崩れない
   *
   * 上限で `stageHost` の縦横比は崩れるが、**その中のステージは描画コアが
   * `contain` で収める**([`computeTransform`](../../../renderer/src/layout.ts) は
   * 幅と高さの両方を見る)。`stageHost` は中央寄せなので、余った分は左右に出る。
   *
   * ## なぜ 2 回代入するのか
   *
   * **`dvh` は `vh` より新しい。** 非対応のブラウザは 2 回目の代入を無視するので、
   * **1 回目の `vh` が残る。**
   *
   * **`vh` だけでは足りない。** モバイルの `vh` は**アドレスバーを含まない
   * 最大の高さ**で、バーが出ている間は画面より大きい —— 報告された
   * 「タブなどではみ出す」がまさにこれ。`dvh` は**いま見えている高さ**に追従する。
   *
   * **`aspect-ratio` は外さない。** 高さを中身依存にすると
   * 「親の高さ ← 中身 ← 親の測定値」で循環し、**永久に 0 のまま**になる(#84)。
   */
  private limitToViewport(): void {
    // **順に代入する。** 非対応の値は捨てられ、前のものが残る
    // (順序の意味は `VIEWPORT_HEIGHT_LIMITS` に書いた)。
    for (const value of VIEWPORT_HEIGHT_LIMITS) {
      this.stageHost.style.maxHeight = value;
    }
  }

  private applyAspectRatio(canvas: { readonly width: number; readonly height: number }): void {
    if (canvas.width <= 0 || canvas.height <= 0) return;
    this.stageHost.style.aspectRatio = `${String(canvas.width)} / ${String(canvas.height)}`;
  }

  private async mountRenderer(template: PublicTemplate): Promise<void> {
    // **描画コアを載せる前に高さを決める。** 0 のまま mount すると
    // ステージが 0×0 で作られる。
    this.applyAspectRatio(template.canvas);

    // 背景があるなら取得できることを確かめる。**変換中は 404 が返りうる。**
    if (template.background !== null) {
      const ok = await this.waitForBackground(template.background.url);
      if (this.destroyed) return;
      if (!ok) {
        // 上限を超えたら手動再試行へ切り替える(#33)。
        this.fail(VIEWER_ERROR_CODES.NETWORK);
        return;
      }
    }

    /*
     * **空のセルに案内を出す**(#208)。
     *
     * ビューアは**取引先のサイトで初見の訪問者が触る画面**で、素材を入れるまで
     * 背景写真が 1 枚出ているだけだった —— **D&D が発見できないと
     * 機能に到達できない**(割り当ては #124 / #159 で D&D だけに絞った)。
     *
     * **S04 は渡さない。** 編集ハンドルとレイヤーパネルがあるので要らない。
     */
    const hints = messages(this.lang);
    this.renderer = createRenderer({
      fit: this.options.fit ?? 'contain',
      emptyCellHint: { drop: hints.emptyCellDrop, touch: hints.emptyCellTouch },
    });
    this.renderer.mount(
      this.stageHost,
      {
        canvas: template.canvas,
        backgroundUrl: template.background?.url ?? null,
        videoFrames: template.videoFrames,
        obstacles: template.obstacles,
      },
      {
        onError: (e) => {
          // **UNSUPPORTED は落とさない。** 背景と輪郭は出ているので、
          // 案内だけ添えて操作は続けさせる。
          this.emit({ type: 'error', code: e.code as ViewerErrorCode });
          if (e.code === VIEWER_ERROR_CODES.UNSUPPORTED) {
            this.showMessage(errorMessage(VIEWER_ERROR_CODES.UNSUPPORTED, this.lang));
          }
        },
        onContextLost: () => {
          // **止めるのは動画だけ**(#158)。
          for (const { source } of this.assignments.values()) {
            if (isVideoSource(source)) source.pause();
          }
          // **位置は保つ** —— 復帰したら続きから流し直せる。
          this.playback = stoppedPlayback(this.playback, Date.now());
          this.updateControls();
        },
        onContextRestored: () => {
          // **割り当てを再実行する。** テクスチャは失われている(#33)。
          for (const { frameId, cellIndex, source } of this.assignments.values()) {
            this.renderer?.setSource(frameId, cellIndex, source);
            if (this.options.autoplay !== false) void tryPlay(source);
          }
          /*
           * **操作 UI を再生中に戻す**(#159)。戻さないと、動画は流れているのに
           * 「停止」が押せず**利用者が止められない。**
           */
          if (this.options.autoplay !== false && this.videos().length > 0) {
            this.playback = autoStartedPlayback(this.playback, Date.now());
          }
          this.updateControls();
        },
        onTransform: (transform) => {
          this.transform = transform;
        },
      },
    );

    this.hideLoading();
    /*
     * **枠が 0 個なら D&D の受け口を出さない**(落とす先がない — #33)。
     *
     * **`ui` ごと隠さない。** `signa-message` が `ui` の中にあるため、
     * 隠すと「この環境では合成表示に対応していません」まで消える ——
     * そして**受け口も消えてドロップがページへ素通りする。**
     */
    this.showDropSurface(template.videoFrames.length > 0);
    this.emit({ type: 'ready', frameCount: template.videoFrames.length });
  }

  /**
   * 背景画像を取得できるまで待つ。
   *
   * **指数バックオフで最大 5 回・上限およそ 30 秒**(#33)。
   * 公開 API の再取得も伴わせる —— TTL 60 秒のキャッシュが古い可能性がある。
   */
  private async waitForBackground(initialUrl: string): Promise<boolean> {
    let url = initialUrl;
    if (await loadImage(url)) return true;

    for (const delay of BACKGROUND_RETRY_DELAYS) {
      this.emit({ type: 'error', code: VIEWER_ERROR_CODES.BACKGROUND_PENDING });
      await this.sleep(delay);
      if (this.destroyed) return false;

      try {
        const fresh = await fetchTemplate(this.options.apiBase, this.options.token);
        this.template = fresh;
        // 変換が完了して URL が変わることも、null に戻ることもある。
        if (fresh.background === null) return true;
        url = fresh.background.url;
      } catch {
        // 取得できなくても画像だけ再試行する価値はある。
      }

      if (await loadImage(url)) return true;
    }
    return false;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.retryTimer = setTimeout(resolve, ms);
    });
  }

  // -------------------------------------------------------------------------
  // 動画の受け渡し
  // -------------------------------------------------------------------------

  /**
   * D&D の受け口と操作の帯を出すか。
   *
   * **`signa-ui` は隠さない。** あれには受け口が入っており、隠したまま
   * 戻し忘れると**ドロップがページへ素通りしてブラウザがファイルを開く**
   * (#159 で実際に起きた)。隠すのは中身だけにする。
   */
  private showDropSurface(visible: boolean): void {
    this.dropzone.hidden = !visible;
    this.actions.hidden = !visible;
  }

  /**
   * D&D を受ける。
   *
   * **受け口はビューア全体(`root`)。** 以前は受け口の矩形だけに付けており、
   * **1px でも外すとブラウザがファイルを開いてしまった**(ドロップの既定動作は
   * 「そのファイルへ移動」)。狙って落とす画面ではないので、
   * **ビューアの上に落ちたものはすべて受ける。**
   *
   * どのセルに入るかは `pickCell` が決める —— 枠の外に落としても
   * 空きセルへ回す(#33 の「とりあえず落とした操作を失敗にしない」)。
   */
  private bindDragAndDrop(): void {
    // **`dragover` で preventDefault しないとドロップが発火しない。**
    // しないままだと `drop` が来ず、ブラウザがファイルへ移動する。
    this.root.addEventListener('dragover', (event) => {
      event.preventDefault();
      if (event.dataTransfer !== null) event.dataTransfer.dropEffect = 'copy';
      this.showDropTarget(this.dropPoint(event));
    });

    this.root.addEventListener('dragleave', (event) => {
      // **子要素をまたぐたびに消さない。** ビューアの外へ出たときだけ。
      const to = event.relatedTarget;
      if (to instanceof Node && this.root.contains(to)) return;
      this.hideDropTarget();
    });

    this.root.addEventListener('drop', (event) => {
      event.preventDefault();
      this.hideDropTarget();
      const file = event.dataTransfer?.files[0];
      if (file === undefined) return;
      void this.acceptFile(file, this.dropPoint(event));
    });
  }

  /**
   * ドロップ先になる**セル**を縁取る(#124 → #152)。
   *
   * **落とす前に入る先を見せる。** 以前は画面全体を縁取っていて、
   * どの枠に入るのか分からなかった。分割した盤面では枠全体でも足りない ——
   * **どのセルに入るか**まで見せる。
   *
   * **`acceptFile` と同じ `pickCell` を通す** —— 表示と実際の行き先が
   * 食い違わないようにする。
   */
  private showDropTarget(position: { x: number; y: number } | null): void {
    const frames: readonly FrameLike[] = this.template?.videoFrames ?? [];
    const normalized =
      position === null
        ? null
        : toNormalized(position, { width: this.transform.width, height: this.transform.height });
    const target = this.pick(normalized, frames);

    if (target === null || this.transform.width <= 0) {
      this.hideDropTarget();
      return;
    }

    const frame = frames.find((f) => f.id === target.frameId);
    if (frame === undefined) {
      this.hideDropTarget();
      return;
    }

    // セルの輪郭が出せなければ枠全体に落とす(潰れたクアッドなど)。
    const outline = cellOutline(frame.quad, frame.grid, target.cellIndex) ?? frame.quad;

    const { width, height, offsetX, offsetY } = this.transform;
    this.dropOutline.setAttribute('width', String(width));
    this.dropOutline.setAttribute('height', String(height));
    this.dropOutline.style.left = `${String(offsetX)}px`;
    this.dropOutline.style.top = `${String(offsetY)}px`;
    this.dropOutlineShape.setAttribute('points', outlinePoints(outline, { width, height }));
    this.dropzone.dataset.active = 'true';
  }

  /**
   * ドロップ先のセルを決める。**表示と実際の割り当てで同じものを使う。**
   *
   * どのセルかは描画コアの [`cellAt`](../../../renderer/src/hit-test.ts) に
   * 委ねる —— 盤面は台形なので、逆写像なしには決められない(ADR-0029)。
   */
  private pick(point: NormalizedPoint | null, frames: readonly FrameLike[]): CellRef | null {
    return pickCell(point, frames, new Set(this.assignments.keys()), (frame, at) =>
      cellAt(at, frame.quad, frame.grid),
    );
  }

  private hideDropTarget(): void {
    this.dropzone.dataset.active = 'false';
  }

  /** ドロップ位置をステージのローカル座標にする。 */
  private dropPoint(event: DragEvent): { x: number; y: number } | null {
    const rect = this.dropzone.getBoundingClientRect();
    if (this.transform.width <= 0) return null;
    // ステージは中央寄せなので、余白分を引く。
    const offsetX = (rect.width - this.transform.width) / 2;
    const offsetY = (rect.height - this.transform.height) / 2;
    return {
      x: event.clientX - rect.left - offsetX,
      y: event.clientY - rect.top - offsetY,
    };
  }

  /**
   * 再生・停止を埋め込み先へ通知する見張りを張る(#168 で作り直した)。
   *
   * ## なぜ素材ごとに 1 度だけか
   *
   * 以前はドロップのたびに `target.frameId` を閉じ込んだリスナを張っていた。
   * **共有すると同じ要素にセルの数だけ積み**、しかも**外す経路が無い** ——
   * その素材を持たなくなったセルの `play` / `pause` が飛び続ける。
   *
   * **発火時に「いまその素材を載せているセル」を引く。** こうすると、
   * 共有していないときの挙動(1 セル = 1 通知)がそのまま保たれる。
   */
  private watchPlayback(source: RendererSource): void {
    if (this.watched.has(source)) return;
    this.watched.add(source);
    const notify = (type: 'play' | 'pause'): void => {
      for (const assignment of this.assignments.values()) {
        if (assignment.source === source) this.emit({ type, frameId: assignment.frameId });
      }
    };
    source.addEventListener('play', () => {
      notify('play');
    });
    source.addEventListener('pause', () => {
      notify('pause');
    });
  }

  private async acceptFile(file: File, position: { x: number; y: number } | null): Promise<void> {
    const m = messages(this.lang);
    const kind = sourceKindOf(file);
    if (kind === null) {
      this.showMessage(m.notSupported);
      return;
    }

    const frames: readonly FrameLike[] = this.template?.videoFrames ?? [];
    const normalized =
      position === null
        ? null
        : toNormalized(position, { width: this.transform.width, height: this.transform.height });
    const target = this.pick(normalized, frames);
    if (target === null) return;

    /*
     * **同じファイルなら要素を使い回す**(#168 /
     * [ADR-0031](../../../../../docs/adr/0031-source-keyed-upload.md) の決定 4)。
     *
     * ドロップごとに `<video>` を作ると、同じ動画を 4 セルに入れただけで
     * **復号器が 4 本**立つ。同時に使えるハードウェア復号器には上限があり、
     * 超えるとソフトウェア復号に落ちて CPU が張り付く ——
     * 「一部の動画だけ進みが遅い」がこの形で起こる。
     *
     * **共有すると再生位置も共有される。** 同じ動画は必ず頭がそろう
     * (#159 の同期と矛盾しない)。
     */
    const fileKey = fileKeyOf(file);
    const shared = findShared(this.assignments.values(), fileKey);
    const source = shared ?? createRendererSource(file, kind);
    // **使い回すときは差し込まない。** 既に `videoHost` の中にある。
    if (shared === null) this.videoHost.append(source);

    if (!(await waitForNaturalSize(source))) {
      // **使い回した要素は捨てない。** 他のセルがまだ使っている。
      if (shared === null) destroyRendererSource(source);
      // **種類ごとに理由を分ける。** 「動画形式が再生できない」と
      // 画像に言われても直しようがない。
      this.showMessage(kind === 'video' ? m.videoUnsupported : m.imageUnsupported);
      this.emit({ type: 'error', code: VIEWER_ERROR_CODES.VIDEO_UNSUPPORTED });
      return;
    }
    if (this.destroyed) {
      if (shared === null) destroyRendererSource(source);
      return;
    }

    /*
     * **大きすぎる素材は貼れない**(#158)。GPU のテクスチャの上限を超えると
     * `texImage2D` が失敗し、**セルが空のまま**になる ——
     * 「入れたのに出ない」が最も分かりにくいので、理由を出して弾く。
     */
    const limit = this.renderer?.maxSourceSize() ?? 0;
    if (exceedsTextureLimit(naturalSizeOf(source), limit)) {
      if (shared === null) destroyRendererSource(source);
      this.showMessage(m.tooLargeLimit.replace('{n}', String(limit)));
      // **他の失敗経路と同じく知らせる。** ここだけ黙ると埋め込み先が取りこぼす。
      this.emit({ type: 'error', code: VIEWER_ERROR_CODES.VIDEO_UNSUPPORTED });
      return;
    }

    /*
     * **共有元が待っている間に捨てられていないか確かめる**(#168)。
     *
     * `findShared` は `await` の前に評価している。3 回以上のドロップが
     * 絡むと、掴んだ要素がその間に破棄されている経路が残る ——
     * 破棄済みの要素を貼っても**何も出ない**ので、やり直してもらう。
     */
    if (shared !== null && !isStillUsed(this.assignments.values(), shared)) {
      this.showMessage(m.retryAssign);
      return;
    }

    // **同じセルに 2 回ドロップしたら差し替える**(#33)。
    const key = cellKey(target);
    const previous = this.assignments.get(key);
    this.assignments.set(key, { ...target, source, fileKey });
    /*
     * **前の要素は「誰も使っていなければ」破棄する**(#168)。
     *
     * 共有しているのに捨てると**他のセルの絵が消える。** 判定は表から
     * 導出する —— 参照カウンタは表と食い違っても気づけない。
     *
     * **`set` のあとに見る。** 先に見ると、差し替え先が同じ要素だった場合に
     * 「まだ使っている」を取りこぼす。
     */
    if (previous !== undefined) {
      destroyUnused([previous], this.assignments.values(), destroyRendererSource);
    }
    this.renderer?.setSource(target.frameId, target.cellIndex, source);
    this.showMessage(null);
    // **操作ボタンと注意表示をそろえる**(#159)。
    this.updateControls();

    this.emit({
      type: 'videoassigned',
      frameId: target.frameId,
      cellIndex: target.cellIndex,
      frameCount: frames.length,
      cellCount: totalCells(frames),
      assignedCount: this.assignments.size,
    });

    // **再生の通知は動画にだけ。** 静止画には再生の概念がない(#158)。
    if (!isVideoSource(source)) return;

    /*
     * **走っている時間軸に合わせてから再生する**(#123)。
     * 合わせないと「当てた順にずれる」—— まさに報告された症状になる。
     */
    const join = joinPositionOf(this.playback, Date.now());
    if (join !== null) {
      const to = correctionsFor([source], join)[0]?.to;
      if (to !== undefined) source.currentTime = to;
    }

    this.watchPlayback(source);

    if (this.options.autoplay === false) return;
    if (!(await tryPlay(source))) {
      /*
       * **自動再生が拒否された。** ミュートでも失敗する環境がある(#33)。
       *
       * **専用のボタンは出さない**(#159)—— 操作 UI の「再生」がその役目を
       * 兼ねる。2 つ出すと「どちらを押せばよいか」が分からない。
       */
      // **押す先は操作 UI の「再生」。** 専用ボタンは出さない(#159)。
      this.showMessage(m.playFailed);
      return;
    }
    /*
     * 自動再生できた。**時間軸も一緒に動かす**(#159)。
     *
     * ここを忘れると**見張りが何もせず、次の「再生」で全部が頭へ戻る。**
     */
    this.playback = autoStartedPlayback(this.playback, Date.now());
    this.updateControls();
  }

  // -------------------------------------------------------------------------
  // 再生の操作(#159)
  // -------------------------------------------------------------------------

  /** 操作ボタンを 1 つ作る。**クリックは呼び出し側が決める。** */
  private makeControl(label: string, onClick: () => void): HTMLButtonElement {
    const button = el('button', 'signa-button');
    button.textContent = label;
    button.addEventListener('click', onClick);
    return button;
  }

  /** 再生の対象になる素材(**動画だけ** — #158)。 */
  private videos(): HTMLVideoElement[] {
    return playableOf(this.assignments.values());
  }

  /**
   * まとめて再生する。
   *
   * `fromStart` のときは**時間軸を先頭に戻す**(= 同期)。そうでなければ
   * 止めた位置から続ける。
   *
   * **1 本でも拒否されたら知らせる。** ミュートでも自動再生が拒否される
   * 環境がある(#33 のモバイル対処)。
   */
  private async playAll(fromStart: boolean): Promise<void> {
    const videos = this.videos();
    if (videos.length === 0) return;

    this.playback = startedPlayback(this.playback, Date.now(), fromStart);
    /*
     * **「同期」のときだけ位置を触る**(#172)。
     *
     * 以前は再開時にも時間軸へ合わせ直していたが、**止めた時点でそろって
     * いるので、そのまま続ければそろったまま**である。
     * `currentTime` への代入は**シーク**なので、**触らないほうが速いし正しい。**
     */
    if (fromStart) for (const video of videos) video.currentTime = 0;

    this.updateControls();

    const results = await Promise.allSettled(videos.map((video) => video.play()));

    /*
     * **割り込まれたら何も言わない。** `play()` の約束は `pause()` に
     * 割り込まれると reject する(HTML の仕様)—— 「再生」の直後に「停止」を
     * 押しただけで**偽の失敗**が出てしまう。
     */
    if (this.destroyed || !this.playback.playing) return;

    const m = messages(this.lang);
    if (results.some((r) => r.status === 'rejected')) {
      this.showMessage(m.playFailed);
    } else if (this.message.textContent === m.playFailed) {
      // **自分が出した失敗だけ消す。** 他の案内(未対応など)は残す。
      this.showMessage(null);
    }
  }

  /** まとめて止める。**位置は保つ**(再生で続きから始まる)。 */
  private pauseAll(): void {
    this.playback = stoppedPlayback(this.playback, Date.now());
    for (const video of this.videos()) video.pause();
    this.updateControls();
  }

  /**
   * 操作ボタンと注意表示を今の割り当てに合わせる。
   *
   * **割り当てが変わるたびに呼ぶ。** 判断は
   * [`playback.ts`](./playback.ts) の純関数に置いてある ——
   * DOM 構築は jsdom で試さないため(ADR-0019)。
   */
  private updateControls(): void {
    const state = controlsState(this.videos().length, this.playback.playing);
    this.controls.hidden = !state.visible;
    this.playButton.disabled = !state.canPlay;
    this.pauseButton.disabled = !state.canPause;
    this.syncButton.disabled = !state.canSync;

    // **素材を当てたら注意表示を消す**(#159)。すべて解除したら戻す。
    this.notice.hidden = !showsNoUploadNotice(this.assignments.size);
  }

  // -------------------------------------------------------------------------
  // 状態表示
  // -------------------------------------------------------------------------

  private showLoading(): void {
    this.status.hidden = false;
    this.status.classList.remove('signa-status-error');
    this.status.replaceChildren(document.createTextNode(messages(this.lang).loading));
  }

  private hideLoading(): void {
    this.status.hidden = true;
  }

  private fail(code: ViewerErrorCode): void {
    this.emit({ type: 'error', code });

    const text = errorMessage(code, this.lang);
    if (text === null) {
      this.status.hidden = true;
      return;
    }

    this.status.hidden = false;
    this.status.classList.add('signa-status-error');
    this.status.replaceChildren(document.createTextNode(text));

    if (isRetryable(code)) {
      const retry = el('button', 'signa-button');
      retry.textContent = messages(this.lang).retry;
      retry.addEventListener('click', () => {
        void this.start();
      });
      this.status.append(retry);
    }
  }

  private showMessage(text: string | null): void {
    if (text === null) {
      this.message.hidden = true;
      this.message.textContent = '';
      return;
    }
    this.message.hidden = false;
    this.message.textContent = text;
  }

  private emit(event: ShellEvent): void {
    this.options.onEvent?.(event);
  }

  /** 描画コアと動画を解放する。**埋め込み先が要素を外したときに必ず呼ぶ。** */
  destroy(): void {
    this.destroyed = true;
    if (this.retryTimer !== null) clearTimeout(this.retryTimer);
    this.retryTimer = null;

    // **共有していても 1 度だけ捨てる**(#168)。残るものは無い(全部捨てる)。
    destroyUnused(this.assignments.values(), [], destroyRendererSource);
    this.assignments.clear();

    this.renderer?.destroy();
    this.renderer = null;
    this.root.remove();
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  return node;
}
